import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { fractionalLeaveService } from '@/api/domains/fractionalLeave/fractionalLeave.service';
import { FRACTIONAL_LEAVE_LIST_REFRESH_EVENT } from '@/api/domains/fractionalLeave/fractionalLeave.types';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import { normalizeHoursForBackend } from '@/utils';
import {
  createFractionalLeaveFormSchema,
  type CreateFractionalLeaveFormValues,
} from '@/schemas/fractionalLeave/createFractionalLeaveForm';


// ── Hook params ────────────────────────────────────────────────────────────

export interface UseCreateFractionalLeaveFormParams {
  onSubmit?: () => void;
}

// ── Return type interface ──────────────────────────────────────────────────

export interface UseCreateFractionalLeaveFormResult {
  nameValue: string;
  descriptionValue: string;
  hoursValue: string;
  deductibleValue: boolean;
  handleNameChange: (value: string) => void;
  handleDescriptionChange: (value: string) => void;
  handleHoursChange: (value: string) => void;
  handleDeductibleChange: (checked: boolean) => void;
  handleSubmit: (event?: FormEvent<HTMLFormElement>) => void;
  isSubmitting: boolean;
  isReady: boolean;
  errors: FieldErrors<CreateFractionalLeaveFormValues>;
  isSubmitted: boolean;
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function useCreateFractionalLeaveForm({
  onSubmit,
}: UseCreateFractionalLeaveFormParams): UseCreateFractionalLeaveFormResult {
  const { t: translate } = useTranslation();
  const { showNotification, closeSidePanel, showApiError } = useUI();

  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Local field state ──────────────────────────────────────────────────
  const [nameValue, setNameValue] = useState('');
  const [descriptionValue, setDescriptionValue] = useState('');
  const [hoursValue, setHoursValue] = useState('');
  const [deductibleValue, setDeductibleValue] = useState(false);

  // ── React Hook Form ────────────────────────────────────────────────────
  const {
    handleSubmit: rhfHandleSubmit,
    setValue,
    setError,
    register,
    unregister,
    formState,
  } = useForm<CreateFractionalLeaveFormValues>({
    resolver: zodResolver(createFractionalLeaveFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      description: '',
      hours: '',
      deductible: false,
    },
  });

  // ── Register fields ────────────────────────────────────────────────────
  useEffect(() => {
    register('name');
    register('description');
    register('hours');
    register('deductible');
    return () => {
      unregister('name');
      unregister('description');
      unregister('hours');
      unregister('deductible');
    };
  }, [register, unregister]);

  // ── Sync local state → RHF form ──────────────────────────────────────────
  // Needed to re-establish form values after React StrictMode re-registration
  // (unregister → register resets values; state persists but form does not).
  useEffect(() => {
    setValue('name', nameValue, { shouldValidate: false });
    setValue('description', descriptionValue, { shouldValidate: false });
    setValue('hours', hoursValue, { shouldValidate: false });
    setValue('deductible', deductibleValue, { shouldValidate: false });
  }, [nameValue, descriptionValue, hoursValue, deductibleValue, setValue]);

  // ── isReady ────────────────────────────────────────────────────────────
  const fieldsRegisteredRef = useRef(false);
  useEffect(() => {
    fieldsRegisteredRef.current = true;
    return () => { fieldsRegisteredRef.current = false; };
  }, []);

  const isReady = useMemo(
    () =>
      fieldsRegisteredRef.current &&
      nameValue.trim().length > 0 &&
      descriptionValue.trim().length > 0,
    [nameValue, descriptionValue],
  );

  // ── Field change handlers ──────────────────────────────────────────────
  const handleNameChange = useCallback((value: string) => {
    setNameValue(value);
    setValue('name', value, { shouldDirty: true });
  }, [setValue]);

  const handleDescriptionChange = useCallback((value: string) => {
    setDescriptionValue(value);
    setValue('description', value, { shouldDirty: true });
  }, [setValue]);

  const handleHoursChange = useCallback((value: string) => {
    setHoursValue(value);
    setValue('hours', value, { shouldDirty: true });
  }, [setValue]);

  const handleDeductibleChange = useCallback((checked: boolean) => {
    setDeductibleValue(checked);
    setValue('deductible', checked, { shouldDirty: true });
  }, [setValue]);

  // ── Submit handler ─────────────────────────────────────────────────────
  const onValid = useCallback(
    async (data: CreateFractionalLeaveFormValues) => {
      setIsSubmitting(true);

      try {
        const response = await fractionalLeaveService.create({
          name: data.name,
          description: data.description,
          hours: normalizeHoursForBackend(data.hours),
          deductible: data.deductible,
        });

        if (response.success) {
          showNotification('success', translate('fractionalLeave:messages.createSuccess'));
          closeSidePanel();
          window.dispatchEvent(new CustomEvent(FRACTIONAL_LEAVE_LIST_REFRESH_EVENT));
          onSubmit?.();
        } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          injectValidationErrors(response.error.validationErrors, { setError });
        } else {
          showApiError(response);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [showNotification, showApiError, translate, closeSidePanel, setError, onSubmit],
  );

  const onInvalid = useCallback((): void => {
    // Intentionally empty — errors displayed via isSubmitted guard
  }, []);

  const handleSubmit = useCallback(
    (event?: FormEvent<HTMLFormElement>) => {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
        event.stopPropagation();
      }
      void rhfHandleSubmit(onValid, onInvalid)(event as FormEvent<HTMLFormElement>);
    },
    [rhfHandleSubmit, onValid, onInvalid],
  );

  return {
    nameValue,
    descriptionValue,
    hoursValue,
    deductibleValue,
    handleNameChange,
    handleDescriptionChange,
    handleHoursChange,
    handleDeductibleChange,
    handleSubmit,
    isSubmitting,
    isReady,
    errors: formState.errors,
    isSubmitted: formState.isSubmitted,
  };
}
